import * as React from 'react';
import { st, classes } from './ActionsMenuLayout.st.css';
import { TPAComponentsConsumer } from '../TPAComponentsConfig';
import { ActionsMenuLayoutItem } from './Item/ActionsMenuLayoutItem';
import { ActionsMenuLayoutDivider } from './ActionsMenuLayoutDivider/ActionsMenuLayoutDivider';
import { KEYS } from '../../common/keyCodes';
import {
  ActionsMenuLayoutProps,
  ActionsMenuLayoutLiRefs,
  ActionsMenuLayoutDataHooks,
  ActionsMenuLayoutState,
} from './types';

/** ActionsMenuLayout */
export class ActionsMenuLayout extends React.Component<
  ActionsMenuLayoutProps,
  ActionsMenuLayoutState
> {
  static Item = ActionsMenuLayoutItem;
  static Divider = ActionsMenuLayoutDivider;

  static displayName = 'ActionsMenuLayout';

  state: ActionsMenuLayoutState = {
    focusedIdx: this.props.focusedIndex,
  };

  liRefs: ActionsMenuLayoutLiRefs = {};

  componentDidMount() {
    const { focusedIndex } = this.props;
    if (typeof focusedIndex !== 'undefined') {
      this._focusItem(focusedIndex);
    }
  }

  getDataAttributes(mobile: boolean) {
    return {
      [ActionsMenuLayoutDataHooks.mobile]: mobile,
    };
  }

  _getNextItemIdx(direction: 1 | -1): number {
    const nextItem = this.state.focusedIdx + direction;

    if (direction === 1) {
      return nextItem === Object.values(this.liRefs).length ? 0 : nextItem;
    }

    return nextItem < 0 ? Object.values(this.liRefs).length - 1 : nextItem;
  }

  _addRef(ref, index: number) {
    if (ref) {
      this.liRefs[index] = ref;
    }
  }

  _focusItem(idx: number) {
    this.setState(
      {
        focusedIdx: idx,
      },
      () => {
        Object.values(this.liRefs)[idx]?.liRef?.current.focus();
      },
    );
  }

  _onKeyDown: React.KeyboardEventHandler<HTMLUListElement> = (event) => {
    if (event.key === KEYS.ArrowDown || event.key === KEYS.ArrowDownIE) {
      event.preventDefault(); // don't scroll page on key down
      this._focusItem(this._getNextItemIdx(1));
    }

    if (event.key === KEYS.ArrowUp || event.key === KEYS.ArrowUpIE) {
      event.preventDefault(); // don't scroll page on key down
      this._focusItem(this._getNextItemIdx(-1));
    }
  };

  _onFocus: React.FocusEventHandler<any> = (event) => {
    const focusOnChild = !!Object.values(this.liRefs).find(
      (i) => i.liRef?.current === event.target,
    );
    // focus on initial list item after tab focusing
    if (!focusOnChild) {
      this._focusItem(0);
    }
  };

  // prevent firing focus after mouse click events
  _preventFocus: React.MouseEventHandler<any> = (event) =>
    event.preventDefault();

  render() {
    const { alignment, children, className } = this.props;
    return (
      <TPAComponentsConsumer>
        {({ mobile }) => (
          <ul
            onFocus={this._onFocus}
            onMouseDown={this._preventFocus}
            onKeyDown={this._onKeyDown}
            className={st(classes.root, { mobile }, className)}
            {...this.getDataAttributes(mobile)}
            data-hook={this.props['data-hook']}
            role="menu"
            aria-labelledby={this.props['aria-labeledby']}
            aria-label={this.props['aria-label']}
            tabIndex={0}
          >
            {React.Children.map(children, (child: React.ReactElement, index) =>
              child.type === ActionsMenuLayoutItem
                ? React.cloneElement(child, {
                    className: classes.item,
                    alignment,
                    ref: (_ref) => {
                      this._addRef(_ref, index);
                    },
                  })
                : child,
            )}
          </ul>
        )}
      </TPAComponentsConsumer>
    );
  }
}
